import { DEFAULT_MIVO_LOCALE, normalizeStoredLocale, readStoredLocale } from "@mivo/i18n";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { useAuth } from "@/domains/shared/hooks/useAuth";

/**
 * Forces pt-BR on `/admin/*` without clearing stored preference.
 * Restores preferred locale only when leaving admin — never on language-switch remounts
 * (stale `user.preferredLocale` must not overwrite a just-selected locale).
 */
export function AdminLocaleGuard() {
  const location = useLocation();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isAdminRoute = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  const wasAdminRoute = useRef(isAdminRoute);

  useEffect(() => {
    if (isAdminRoute) {
      wasAdminRoute.current = true;
      if (i18n.language !== DEFAULT_MIVO_LOCALE) {
        void i18n.changeLanguage(DEFAULT_MIVO_LOCALE);
      }
      return;
    }

    if (!wasAdminRoute.current) {
      return;
    }

    wasAdminRoute.current = false;
    const preferred = normalizeStoredLocale(user?.preferredLocale ?? readStoredLocale());
    if (i18n.language !== preferred) {
      void i18n.changeLanguage(preferred);
    }
  }, [i18n, isAdminRoute, user?.preferredLocale]);

  return null;
}
